/**
 * Hook to fix invalid SVG attributes in dynamically injected ad content
 * 
 * This hook monitors the DOM for SVG elements with invalid width/height="auto" attributes
 * and fixes them to prevent console errors.
 */

import { useEffect, useRef } from 'react';
import { sanitizeAdHtml } from '../utils/adSanitizer';
// import { logger } from "../utils/logger";
export const useAdSvgFixer = () => {
  const observerRef = useRef<MutationObserver | null>(null);
  const processedElementsRef = useRef<WeakSet<Element>>(new WeakSet());

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    // Function to fix SVG attributes in an element and its children
    const fixSvgAttributes = (element: Element) => {
      // Find all SVG elements
      const svgElements = element.querySelectorAll('svg');
      
      svgElements.forEach((svg) => {
        // Skip if already processed
        if (processedElementsRef.current.has(svg)) {
          return;
        }

        // Fix width attribute
        const width = svg.getAttribute('width');
        if (width === 'auto' || width === 'Auto' || width === 'AUTO') {
          svg.removeAttribute('width');
          const currentStyle = svg.getAttribute('style') || '';
          if (!currentStyle.includes('width')) {
            const newStyle = currentStyle 
              ? `${currentStyle}; width: 100%;`.replace(/^;\s*/, '').replace(/;\s*;/g, ';')
              : 'width: 100%;';
            svg.setAttribute('style', newStyle);
          }
        }

        // Fix height attribute
        const height = svg.getAttribute('height');
        if (height === 'auto' || height === 'Auto' || height === 'AUTO') {
          svg.removeAttribute('height');
          const currentStyle = svg.getAttribute('style') || '';
          if (!currentStyle.includes('height')) {
            const newStyle = currentStyle 
              ? `${currentStyle}; height: auto;`.replace(/^;\s*/, '').replace(/;\s*;/g, ';')
              : 'height: auto;';
            svg.setAttribute('style', newStyle);
          }
        }

        // Mark as processed
        processedElementsRef.current.add(svg);
      });

      // Also check if the element itself is an SVG
      if (element.tagName === 'SVG') {
        if (!processedElementsRef.current.has(element)) {
          const width = element.getAttribute('width');
          if (width === 'auto' || width === 'Auto' || width === 'AUTO') {
            element.removeAttribute('width');
            const currentStyle = element.getAttribute('style') || '';
            if (!currentStyle.includes('width')) {
              const newStyle = currentStyle 
                ? `${currentStyle}; width: 100%;`.replace(/^;\s*/, '').replace(/;\s*;/g, ';')
                : 'width: 100%;';
              element.setAttribute('style', newStyle);
            }
          }

          const height = element.getAttribute('height');
          if (height === 'auto' || height === 'Auto' || height === 'AUTO') {
            element.removeAttribute('height');
            const currentStyle = element.getAttribute('style') || '';
            if (!currentStyle.includes('height')) {
              const newStyle = currentStyle 
                ? `${currentStyle}; height: auto;`.replace(/^;\s*/, '').replace(/;\s*;/g, ';')
                : 'height: auto;';
              element.setAttribute('style', newStyle);
            }
          }

          processedElementsRef.current.add(element);
        }
      }
    };

    // Initial fix for existing SVGs
    if (document.body) {
      fixSvgAttributes(document.body);
    }

    // Set up MutationObserver to catch dynamically added SVGs
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Fix SVG attributes in the added element
              fixSvgAttributes(element);
            }
          });
        } else if (mutation.type === 'attributes') {
          // If an SVG attribute was changed, check if it needs fixing
          const target = mutation.target as Element;
          if (target.tagName === 'SVG') {
            fixSvgAttributes(target);
          }
        }
      });
    });

    // Observe the document body for changes
    if (document.body) {
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['width', 'height', 'style']
      });
    }

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);
};

